import {
  clampCropPosition,
  type CropViewportState,
  getContainScale,
  getCropViewportDimensions,
  getMaxZoomForImage,
  IMAGE_CROP_MIN_ZOOM,
  loadImage,
  BANNER_ASPECT_RATIO,
} from "./cropImageFile";

const MATCH_WIDTH = 56;

function imageDataDiff(a: ImageData, b: ImageData): number {
  let sum = 0;
  for (let i = 0; i < a.data.length; i += 4) {
    const dr = a.data[i] - b.data[i];
    const dg = a.data[i + 1] - b.data[i + 1];
    const db = a.data[i + 2] - b.data[i + 2];
    const da = a.data[i + 3] - b.data[i + 3];
    sum += dr * dr + dg * dg + db * db + da * da;
  }
  return sum;
}

function extractThumb(
  image: HTMLImageElement,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  thumbW: number,
  thumbH: number
): ImageData {
  const canvas = document.createElement("canvas");
  canvas.width = thumbW;
  canvas.height = thumbH;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Unable to read image data");
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, thumbW, thumbH);
  return ctx.getImageData(0, 0, thumbW, thumbH);
}

/**
 * Render what the crop modal / export would produce for a given zoom+position,
 * including letterboxing when the image does not fill the frame (zoom ≈ 1).
 */
function renderViewportThumb(
  original: HTMLImageElement,
  position: { x: number; y: number },
  effectiveScale: number,
  viewportWidth: number,
  viewportHeight: number,
  thumbW: number,
  thumbH: number
): ImageData {
  const canvas = document.createElement("canvas");
  canvas.width = thumbW;
  canvas.height = thumbH;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Unable to read image data");
  }

  // Match cropImageToFile: transparent letterbox (RGB 0 in ImageData).
  ctx.clearRect(0, 0, thumbW, thumbH);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const scaleX = thumbW / viewportWidth;
  const scaleY = thumbH / viewportHeight;
  ctx.setTransform(scaleX, 0, 0, scaleY, 0, 0);
  ctx.drawImage(
    original,
    position.x,
    position.y,
    original.naturalWidth * effectiveScale,
    original.naturalHeight * effectiveScale
  );
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  return ctx.getImageData(0, 0, thumbW, thumbH);
}

function sampleAxis(min: number, max: number, steps: number): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return [0];
  }
  if (steps <= 0 || Math.abs(max - min) < 0.5) {
    return [(min + max) / 2];
  }
  const values: number[] = [];
  for (let i = 0; i <= steps; i += 1) {
    values.push(min + ((max - min) * i) / steps);
  }
  return values;
}

/** Pan range matching clampCropPosition (letterbox → locked at 0). */
function getPanRange(displaySize: number, viewportSize: number) {
  if (displaySize <= viewportSize) {
    return { min: 0, max: 0 };
  }
  return { min: viewportSize - displaySize, max: 0 };
}

function findBestViewportState(
  original: HTMLImageElement,
  croppedThumb: ImageData,
  aspectRatio: number,
  fixedViewport?: { width?: number; height?: number } | null
): CropViewportState | null {
  const origW = original.naturalWidth;
  const origH = original.naturalHeight;
  const { viewportWidth, viewportHeight } = getCropViewportDimensions(
    aspectRatio,
    fixedViewport
  );
  const baseScale = getContainScale(origW, origH, viewportWidth, viewportHeight);
  const maxZoom = getMaxZoomForImage(origW, origH, viewportWidth, viewportHeight);
  const thumbW = MATCH_WIDTH;
  const thumbH = Math.max(1, Math.round(MATCH_WIDTH / aspectRatio));

  let bestScore = Infinity;
  let bestState: CropViewportState | null = null;

  for (let zoom = IMAGE_CROP_MIN_ZOOM; zoom <= maxZoom + 0.001; zoom += 0.12) {
    const effectiveScale = baseScale * zoom;
    const displayW = origW * effectiveScale;
    const displayH = origH * effectiveScale;
    const xRange = getPanRange(displayW, viewportWidth);
    const yRange = getPanRange(displayH, viewportHeight);
    const xs = sampleAxis(
      xRange.min,
      xRange.max,
      displayW > viewportWidth ? 12 : 0
    );
    const ys = sampleAxis(
      yRange.min,
      yRange.max,
      displayH > viewportHeight ? 12 : 0
    );

    for (const x of xs) {
      for (const y of ys) {
        const position = clampCropPosition(
          x,
          y,
          effectiveScale,
          origW,
          origH,
          viewportWidth,
          viewportHeight
        );
        const candidate = renderViewportThumb(
          original,
          position,
          effectiveScale,
          viewportWidth,
          viewportHeight,
          thumbW,
          thumbH
        );
        const score = imageDataDiff(candidate, croppedThumb);
        if (score < bestScore) {
          bestScore = score;
          bestState = { zoom, position };
        }
      }
    }
  }

  if (!bestState) {
    return null;
  }

  for (
    let zoom = Math.max(IMAGE_CROP_MIN_ZOOM, bestState.zoom - 0.2);
    zoom <= Math.min(maxZoom, bestState.zoom + 0.2) + 0.0001;
    zoom += 0.04
  ) {
    const effectiveScale = baseScale * zoom;
    const displayW = origW * effectiveScale;
    const displayH = origH * effectiveScale;
    const xRange = getPanRange(displayW, viewportWidth);
    const yRange = getPanRange(displayH, viewportHeight);
    const spanX = Math.max(8, (xRange.max - xRange.min) / 8);
    const spanY = Math.max(8, (yRange.max - yRange.min) / 8);

    const xs = sampleAxis(
      Math.max(xRange.min, bestState.position.x - spanX * 2),
      Math.min(xRange.max, bestState.position.x + spanX * 2),
      displayW > viewportWidth ? 8 : 0
    );
    const ys = sampleAxis(
      Math.max(yRange.min, bestState.position.y - spanY * 2),
      Math.min(yRange.max, bestState.position.y + spanY * 2),
      displayH > viewportHeight ? 8 : 0
    );

    for (const x of xs) {
      for (const y of ys) {
        const position = clampCropPosition(
          x,
          y,
          effectiveScale,
          origW,
          origH,
          viewportWidth,
          viewportHeight
        );
        const candidate = renderViewportThumb(
          original,
          position,
          effectiveScale,
          viewportWidth,
          viewportHeight,
          thumbW,
          thumbH
        );
        const score = imageDataDiff(candidate, croppedThumb);
        if (score < bestScore) {
          bestScore = score;
          bestState = { zoom, position };
        }
      }
    }
  }

  return bestState;
}

export async function deriveCropViewportStateFromImages(
  originalSrc: string,
  croppedSrc: string,
  aspectRatio: number = BANNER_ASPECT_RATIO,
  fixedViewport?: { width?: number; height?: number } | null
): Promise<CropViewportState | null> {
  const originalUrl = String(originalSrc ?? "").trim();
  const croppedUrl = String(croppedSrc ?? "").trim();

  if (!originalUrl || !croppedUrl || originalUrl === croppedUrl) {
    return null;
  }

  const [originalImage, croppedImage] = await Promise.all([
    loadImage(originalUrl),
    loadImage(croppedUrl),
  ]);

  const thumbW = MATCH_WIDTH;
  const thumbH = Math.max(1, Math.round(MATCH_WIDTH / aspectRatio));
  const croppedThumb = extractThumb(
    croppedImage,
    0,
    0,
    croppedImage.naturalWidth,
    croppedImage.naturalHeight,
    thumbW,
    thumbH
  );

  const viewportState = findBestViewportState(
    originalImage,
    croppedThumb,
    aspectRatio,
    fixedViewport
  );

  if (!viewportState) {
    return null;
  }

  return viewportState;
}
